import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

const inlineMd = (t) =>
  t.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? <strong key={i} className="text-stone-900">{part.slice(2, -2)}</strong> : part
  );

const MdBlock = ({ text }) => (
  <div className="space-y-4">
    {(text || "").split(/\n{2,}/).map((block, i) => {
      const b = block.trim();
      if (!b) return null;
      if (b.startsWith("## ")) return <h2 key={i} className="font-serif text-xl sm:text-2xl text-forest mt-2">{inlineMd(b.slice(3))}</h2>;
      if (b.startsWith("# ")) return <h2 key={i} className="font-serif text-2xl text-forest mt-2">{inlineMd(b.slice(2))}</h2>;
      if (/^[-•]\s/m.test(b)) return (
        <ul key={i} className="list-disc pl-5 space-y-1.5 text-stone-600 text-sm sm:text-base">
          {b.split("\n").filter((l) => /^[-•]\s/.test(l.trim())).map((l, j) => <li key={j}>{inlineMd(l.trim().slice(2))}</li>)}
        </ul>
      );
      return <p key={i} className="text-stone-600 text-sm sm:text-base leading-relaxed">{inlineMd(b)}</p>;
    })}
  </div>
);

export default function CategoryLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    setData(null);
    api.get(`/categories/${slug}/landing`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [slug]);

  const c = data?.category;

  useEffect(() => {
    if (!c) return;
    document.title = c.seo_title || `${c.name} | Ata Angora Doğal Gıdalar`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = c.seo_description || c.description || "";
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `https://ataangora.com/kategori/${c.slug}`;
    let k = document.querySelector('meta[name="keywords"]');
    if (c.seo_keywords) {
      if (!k) { k = document.createElement("meta"); k.name = "keywords"; document.head.appendChild(k); }
      k.content = c.seo_keywords;
    }
  }, [c]);

  if (data === null) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-stone-500">Yükleniyor...</div>;
  if (data === false || !c) return (
    <div className="max-w-7xl mx-auto px-4 py-20 text-center">
      <p className="font-serif text-2xl text-stone-700">Kategori bulunamadı</p>
      <Link to="/urunler" className="text-forest font-semibold mt-4 inline-block">Ürünlere dön</Link>
    </div>
  );

  const products = data.products || [];
  const faq = (c.landing_faq || []).filter((f) => f.q && f.a);
  const jsonLd = {
    "@context": "https://schema.org", "@type": "CollectionPage",
    name: c.seo_title || c.name,
    description: c.seo_description || c.description || "",
    ...(faq.length ? { mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) } : {}),
  };

  return (
    <div data-testid="category-landing-page" className="pb-16">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <section className="bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <nav className="text-xs text-cream/70 mb-4"><Link to="/" className="hover:text-gold-light">Ana Sayfa</Link> / <span>{c.name}</span></nav>
            <h1 data-testid="landing-title" className="font-serif text-4xl sm:text-5xl leading-tight">{c.name}</h1>
            {(c.landing_hero || c.description) && <p data-testid="landing-hero" className="mt-4 text-cream/85 text-base sm:text-lg leading-relaxed">{c.landing_hero || c.description}</p>}
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to={`/urunler?kategori=${c.slug}`} data-testid="landing-cta-products" className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-stone-900 font-semibold px-6 py-3.5 rounded-xl transition-colors">Ürünleri İncele <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          {c.image && <div className="rounded-3xl overflow-hidden ring-4 ring-gold/30 aspect-[4/3]"><img src={c.image} alt={c.name} className="w-full h-full object-cover" /></div>}
        </div>
      </section>

      {c.landing_content && (
        <section className="max-w-3xl mx-auto px-4 py-12" data-testid="landing-content">
          <MdBlock text={c.landing_content} />
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-serif text-2xl sm:text-3xl text-stone-900">{c.name} Ürünleri</h2>
          <Link to={`/urunler?kategori=${c.slug}`} className="text-sm font-semibold text-forest hover:underline">Tümünü Gör →</Link>
        </div>
        {products.length > 0 ? (
          <div data-testid="landing-products" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {products.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        ) : <p className="text-sm text-stone-400">Bu kategoride henüz ürün yok.</p>}
      </section>

      {faq.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 py-12" data-testid="landing-faq">
          <h2 className="font-serif text-2xl sm:text-3xl text-stone-900 mb-6">Sık Sorulan Sorular</h2>
          <div className="space-y-3">
            {faq.map((f, i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                <button data-testid={`landing-faq-q-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left font-semibold text-stone-800 text-sm sm:text-base">
                  {f.q}
                  <ChevronDown className={`w-4 h-4 shrink-0 text-stone-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p data-testid={`landing-faq-a-${i}`} className="px-5 pb-4 text-sm text-stone-600 leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
